import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { MessageCircle, Shield, Sparkles, ChevronRight, Heart, User } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { MyErotikaLogo } from '../components/MyErotikaLogo';

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const { t, language } = useLanguage();

  const features = [
    {
      icon: MessageCircle,
      title: t('feature_1_title'),
      description: t('feature_1_desc')
    },
    {
      icon: Sparkles,
      title: t('feature_2_title'),
      description: t('feature_2_desc')
    },
    {
      icon: Shield,
      title: t('feature_3_title'),
      description: t('feature_3_desc')
    }
  ];

  // All available avatars
  const avatars = {
    women: [
      { id: 'sofia', name: 'Sofia', image: 'https://customer-assets.emergentagent.com/job_9f7d983b-d5ea-47db-90c0-d35474cb3a52/artifacts/xflp0jsp_1%20Sofia.png' },
      { id: 'mira', name: 'Mira', image: 'https://customer-assets.emergentagent.com/job_0b13bf67-42b1-4ed1-bc61-82e2f679143e/artifacts/8fvwnfqb_1000024907.png' },
      { id: 'luna', name: 'Luna', image: 'https://customer-assets.emergentagent.com/job_e10036c8-5802-4edb-bc17-ba7f72092b73/artifacts/7c48z9v4_Profil%20Luna.png' },
      { id: 'nina', name: 'Nina', image: 'https://customer-assets.emergentagent.com/job_893d3923-0379-4db6-9a12-aeac12868808/artifacts/tk8381n7_Profil%20Nina.png', comingSoon: true },
      { id: 'lisa', name: 'Lisa', image: 'https://customer-assets.emergentagent.com/job_3caf9ad9-a480-4df3-8c37-db23f4beace4/artifacts/8ly69b39_1000025017.png', comingSoon: true },
      { id: 'coming-soon', name: t('coming_soon'), image: null, comingSoon: true }
    ],
    men: [
      { id: 'alexandre', name: 'Lucas', image: 'https://customer-assets.emergentagent.com/job_9f7d983b-d5ea-47db-90c0-d35474cb3a52/artifacts/9y3jb1o1_Profil%20Lucas.png' },
      { id: 'lucas', name: 'Raphaël', image: 'https://customer-assets.emergentagent.com/job_e10036c8-5802-4edb-bc17-ba7f72092b73/artifacts/a1fxx6dv_Profil%20Raphael.png' },
      { id: 'victor', name: 'Victor', image: 'https://customer-assets.emergentagent.com/job_e10036c8-5802-4edb-bc17-ba7f72092b73/artifacts/xfvcgcbg_Black.png' },
      { id: 'lorenzo', name: 'Lorenzo', image: 'https://customer-assets.emergentagent.com/job_3caf9ad9-a480-4df3-8c37-db23f4beace4/artifacts/tqttig3a_1000025015.png' },
      { id: 'coming-soon', name: t('coming_soon'), image: null, comingSoon: true }
    ],
    couples: [
      { id: 'sofia_alexandre', name: 'Hugo & Clara', image: 'https://customer-assets.emergentagent.com/job_893d3923-0379-4db6-9a12-aeac12868808/artifacts/sm1jpw9o_1000026200.png' },
      { id: 'mira_lucas', name: 'Ethan & Maya', image: 'https://customer-assets.emergentagent.com/job_893d3923-0379-4db6-9a12-aeac12868808/artifacts/10473dm7_1000026201.png' },
      { id: 'elena_bastien', name: 'Sami & Eva', image: 'https://customer-assets.emergentagent.com/job_893d3923-0379-4db6-9a12-aeac12868808/artifacts/nk1t0iri_1000026203.png' },
      { id: 'coming-soon', name: t('coming_soon'), image: null, comingSoon: true }
    ]
  };

  // Component for avatar display
  const AvatarCard = ({ avatar, index }) => {
    const destination = isAuthenticated ? '/select-gender' : '/register';
    const content = (
      <div 
        className={`group relative animate-slideUp stagger-${index + 1} cursor-pointer`}
      >
        <div className={`w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-2 ${avatar.comingSoon ? 'border-dashed border-purple-main/50' : 'border-purple-main/30 group-hover:border-purple-light'} transition-all duration-300 ${!avatar.comingSoon && 'group-hover:scale-110'} flex items-center justify-center ${avatar.comingSoon && !avatar.image ? 'bg-purple-main/10' : ''}`}>
          {avatar.comingSoon && !avatar.image ? (
            <span className="text-3xl">?</span>
          ) : (
            <img 
              src={avatar.image}
              alt={avatar.name}
              className={`w-full h-full object-cover object-top ${avatar.comingSoon ? 'opacity-60 grayscale-[0.2]' : ''}`}
            />
          )}
        </div>
        <p className={`text-center text-xs mt-2 max-w-[100px] ${avatar.comingSoon ? 'text-purple-light/70 italic' : 'text-velvet_text-secondary group-hover:text-purple-light'} transition-colors`}>
          {avatar.name}
        </p>
      </div>
    );
    if (avatar.comingSoon) return content;
    return <Link to={destination}>{content}</Link>;
  };

  return (
    <div className="min-h-screen bg-velvet-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <h1 className="shrink-0"><MyErotikaLogo size="sm" /></h1>
          <div className="flex items-center gap-1.5 sm:gap-4">
            <LanguageSelector />
            {isAuthenticated ? (
              <Link to="/select-gender">
                <Button 
                  className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-4 sm:px-6 text-xs sm:text-sm"
                  data-testid="continue-button"
                >
                  {t('continue')}
                </Button>
              </Link>
            ) : (
              <div className="flex items-center gap-1.5 sm:gap-3">
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-velvet_text-secondary hover:text-velvet_text-primary text-xs sm:text-sm px-2 sm:px-4"
                    data-testid="login-button"
                  >
                    {t('login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap"
                    data-testid="register-button"
                  >
                    {t('register')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1638189330012-44e36a97312a?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-velvet-bg via-transparent to-velvet-bg" />
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-purple-main/20 rounded-full blur-3xl animate-pulse-glow" />
          <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-fuchsia-main/15 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16 items-center">
          <div className="animate-slideUp">
            <h2 className="mb-6">
              <MyErotikaLogo size="xl" />
            </h2>
            <p className="text-xl text-velvet_text-secondary mb-10 max-w-lg leading-relaxed">
              {t('hero_subtitle')}
            </p>
            <Link to={isAuthenticated ? '/select-gender' : '/register'}>
              <Button 
                size="lg"
                className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-10 py-6 text-lg font-semibold shadow-neon hover:scale-105 transition-transform"
                data-testid="get-started-button"
              >
                {t('get_started')}
                <ChevronRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>

          <div className="hidden lg:block relative">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-main/20 to-fuchsia-main/20 rounded-3xl blur-xl" />
            <img 
              src="https://images.unsplash.com/photo-1630873273144-cfa9079fdd72?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
              alt="Abstract"
              className="relative rounded-3xl shadow-2xl opacity-80 w-full h-[500px] object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`glass rounded-2xl p-8 card-hover animate-slideUp stagger-${index + 1}`}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 bg-purple-main/20 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="text-purple-light" size={28} />
                </div>
                <h3 className="font-heading text-xl text-velvet_text-primary mb-3">{feature.title}</h3>
                <p className="text-velvet_text-secondary leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Avatars Gallery Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-dark/10 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6">
          <h3 className="font-heading text-4xl text-center gradient-text mb-4">
            {t('partners_await')}
          </h3>
          <p className="text-velvet_text-secondary text-center mb-12 max-w-2xl mx-auto">
            {t('partners_desc')}
          </p>
          
          {/* Women */}
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <User size={18} className="text-pink-400" />
              <h4 className="text-velvet_text-accent font-medium">
                {t('women')}
              </h4>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {avatars.women.map((avatar, index) => (
                <AvatarCard key={avatar.id} avatar={avatar} index={index} />
              ))}
            </div>
          </div>

          {/* Men */}
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <User size={18} className="text-blue-400" />
              <h4 className="text-velvet_text-accent font-medium">
                {t('men')}
              </h4>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {avatars.men.map((avatar, index) => (
                <AvatarCard key={avatar.id} avatar={avatar} index={index} />
              ))}
            </div>
          </div>

          {/* Couples */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Heart size={18} className="text-fuchsia-400" />
              <h4 className="text-velvet_text-accent font-medium">
                {language === 'fr' ? 'Couples' : 'Couples'}
              </h4>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {avatars.couples.map((avatar, index) => (
                <AvatarCard key={avatar.id} avatar={avatar} index={index} />
              ))}
            </div>
          </div>

          {/* CTA inside avatars section */}
          <div className="mt-12 text-center">
            <Link to={isAuthenticated ? '/select-gender' : '/register'}>
              <Button 
                size="lg"
                className="bg-gradient-to-r from-purple-main to-fuchsia-main hover:opacity-90 text-white rounded-full px-8 py-4 font-semibold shadow-neon hover:scale-105 transition-transform"
              >
                {t('start_experience')}
                <ChevronRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-purple-dark/20 to-transparent" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-heading text-4xl lg:text-5xl text-velvet_text-primary mb-6">
            {t('ready_to_begin')}
          </h3>
          <p className="text-xl text-velvet_text-secondary mb-10">
            {t('join_users')}
          </p>
          <Link to={isAuthenticated ? '/select-gender' : '/register'}>
            <Button 
              size="lg"
              className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-12 py-6 text-lg font-semibold shadow-neon-strong hover:scale-105 transition-transform"
              data-testid="cta-button"
            >
              {t('get_started')}
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-velvet_text-secondary text-sm">
            © 2025 MyErotika. {t('all_rights')}
          </p>
        </div>
      </footer>
    </div>
  );
}
